import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { AccordionItem } from '@shared/models/accordion-item';
import { CurrentUserService, ModalService } from '@shared/services';
import { AccordionSecurityReview } from '../../models/edit-tax-result-approval/accordion-security-review';
import { SecurityTaxResultItemReview } from '../../models/edit-tax-result-approval/security-tax-result-item-review';
import { TaxResultApprovalService } from '../../services/tax-result-approval.service';

@Component({
  standalone: false,
  selector: 'gstar-tax-result-card',
  templateUrl: './tax-result-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxResultCardComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly taxResultApproval = inject(TaxResultApprovalService);
  private readonly modalService = inject(ModalService);
  private readonly currentUserService = inject(CurrentUserService);

  securityReview = model.required<AccordionSecurityReview>();
  isReadonly = input.required<boolean>();

  removeFromSelection = output<AccordionSecurityReview>();
  delete = output<AccordionSecurityReview>();
  isDetailsRendered = signal(false);
  groupedSecurityReviews = signal<SecurityTaxResultItemReview[]>([]);

  isAdmin = this.currentUserService.isAdmin;

  ngOnInit(): void {
    this.isDetailsRendered.set(this.securityReview().isExpanded);

    if (this.isDetailsRendered()) {
      this.groupedSecurityReviews.set(
        this.groupSecurityReviews(this.securityReview().securityTaxResultItems)
      );
    }
  }

  expandCollapseHandler(item: AccordionItem): void {
    if (item.isExpanded && !this.isDetailsRendered()) {
      this.isDetailsRendered.set(true);
      this.groupedSecurityReviews.set(
        this.groupSecurityReviews(this.securityReview().securityTaxResultItems)
      );
    }
  }

  private groupSecurityReviews(
    securityTaxResultItems: SecurityTaxResultItemReview[]
  ): SecurityTaxResultItemReview[] {
    const groups = new Map<string, SecurityTaxResultItemReview>();

    for (const review of securityTaxResultItems) {
      if (groups.has(review.title)) {
        const stored = groups.get(review.title)!;
        stored.items.update(items => [...items, ...review.items()]);
        stored.initialSize = stored.items().length;
        groups.set(review.title, stored);
      } else {
        review.initialSize = review.items().length;
        groups.set(review.title, review);
      }
    }

    return Array.from(groups.values());
  }

  actionMenu: ActionButton<AccordionSecurityReview>[] = [
    {
      label: this.translateService.instant('taxResult.approval.editButton'),
      icon: '',
      command: (item: AccordionSecurityReview): void => {
        this.taxResultApproval.setSecuritiesForRuleEdition(
          this.taxResultApproval.cachedSecuritiesForTaxResultEdition(),
          item
        );
        this.router.navigate(['/portfolio', item.security.securityId, 'edit'], {
          queryParams: {
            transactionType: item.security.transactionType.transactionTypeId,
          },
        });
      },
      showIf: () => !this.isReadonly(),
    },
    {
      label: this.translateService.instant('taxResult.approval.removeButton'),
      icon: '',
      command: (item: AccordionSecurityReview): void => {
        this.removeFromSelection.emit(item);
      },
    },
    {
      label: this.translateService.instant(
        'taxResult.approval.delete.buttonLabel'
      ),
      icon: '',
      command: (item: AccordionSecurityReview): void => {
        const contentModalData = `${item.security.entity.client.name}: ${item.security.transactionType.name} - ${item.security.entity.entityType.name} - ${item.security.entity.entityType.name} - ${item.security.securityType.name} - ${item.security.investmentJurisdiction.country.name}`;

        this.modalService.open(
          ConfirmationModalComponent,
          {
            appModalId: 'delete-confirm-modal',
          },
          {
            title: this.translateService.instant(
              'taxResult.approval.delete.titleModal'
            ),
            content: this.translateService.instant(
              'taxResult.approval.delete.contentModal',
              {
                contentModalData,
              }
            ),
            modalId: 'delete-confirm-modal',
            confirmationFn: () => {
              this.delete.emit(item);
            },
          }
        );
      },
      showIf: () => !this.isReadonly(),
    },
  ];

  // General comments. Not specific to user permissions.
  updateCommentHandler(comment: string): void {
    this.securityReview().comment.set(comment);
    this.securityReview().security.comment = comment;
  }

  // Update comment for admin users - only visible by admins.
  updateAdminCommentHandler(comment: string): void {
    this.securityReview().adminComment?.set(comment);
    this.securityReview().security.adminComment = comment;
  }

  getAdminCommentPlaceholder(): string {
    const security = this.securityReview().security;
    const modifiedBy = security.adminCommentModifiedBy?.trim() ?? '';
    const modifiedDate = security.adminCommentModifiedDate
      ? new Date(security.adminCommentModifiedDate)
      : null;
    const hasValidDate =
      !!modifiedDate && !Number.isNaN(modifiedDate.getTime());

    if (!modifiedBy || !hasValidDate) {
      return this.translateService.instant('upsertSecurity.adminComments');
    }

    return `${modifiedBy} - ${modifiedDate.toLocaleString()}`;
  }

  getAdminCommentText(): string {
    const security = this.securityReview().security;
    const modifiedBy = security.adminCommentModifiedBy?.trim() ?? '';
    const modifiedDate = security.adminCommentModifiedDate
      ? new Date(security.adminCommentModifiedDate)
      : null;
    const hasValidDate =
      !!modifiedDate && !Number.isNaN(modifiedDate.getTime());

    if (!modifiedBy || !hasValidDate) {
      return '';
    }

    return this.securityReview().adminComment?.() ?? '';
  }
}
